// articles/TournamentSetup.js
// "Setting Up Your Tournament" (Sept 30, 2026): the whole path for ONE event, from an
// empty account to final results, for an organizer who bought an Event Pass and has
// never used Tee Time Organizer. It stops where the format guides take over (teams,
// the game, codes, printing, ties) and sends him to the right one.
//
// Linked from the Event Pass Next Steps page (/events/ready), the Guides chooser
// (home and the events list) and the four format guides' "Before you start".
// Button and tab names are quoted as they appear in App.js; if a label changes,
// change it here too.
import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-run-a-golf-tournament-with-tee-time-organizer');

const FORMAT_GUIDES = [
  { slug: 'how-to-set-up-a-captains-choice-golf-tournament', title: 'Captain’s Choice (Scramble)', when: 'Every player hits, the team plays the best shot, one score per team.' },
  { slug: 'how-to-set-up-a-best-ball-golf-tournament', title: 'Best Ball', when: 'Everyone plays his own ball; the team counts its best score or scores on each hole.' },
  { slug: 'how-to-set-up-a-shamble-golf-tournament', title: 'Shamble', when: 'The team picks the best drive, then everyone plays his own ball in.' },
  { slug: 'how-to-set-up-a-stroke-play-golf-tournament', title: 'Stroke Play', when: 'Every player for himself; lowest total wins, net, gross or both.' },
];

const FAQS = [
  {
    q: 'How many golfers does my Event Pass cover?',
    a: 'The pass you bought: up to 72 or up to 250 golfers confirmed on the event. Every golfer counts toward the field, whether a guest or a regular player. If your field grows past 72, use Upgrade to 250 in the Set up event window and pay the difference.',
  },
  {
    q: 'Why are my golfers "guests"?',
    a: 'Guests are free and never count toward your account’s plan, however many there are. They see only the rounds they’re invited to and get no invitations to other tee times. That is exactly right for an event field, so add your golfers as Guest.',
  },
  {
    q: 'What if it rains out?',
    a: 'Edit the tee time and change the date. The pass moves with it, as long as the new date is within 6 months of when you bought the pass.',
  },
  {
    q: 'When is the pass used up?',
    a: 'When you finalize the round. After that the results, the leaderboard and the settlements stay viewable for 30 days. An unused pass expires 6 months after purchase.',
  },
  {
    q: 'Can I get a refund?',
    a: 'Yes, if you cancel before pairings are saved. Contact us and we’ll refund the pass.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. You paid once for one event. Your account stays on the free plan afterwards and nothing is deleted, so next year you can buy another pass and everything is where you left it.',
  },
];

export default function TournamentSetup() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        Setting Up Your Tournament
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Event setup guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        You have an Event Pass and an empty account named after your event. This guide takes you
        from there to final results, in the order the steps have to happen. Steps 1 to 4 are the
        same for every tournament. From step 5 on, your format&rsquo;s own guide takes over: teams,
        the game, scorecards, the leaderboard and ties.
      </P>

      <StepTracker
        storageKey="tto_guide_tournament_steps"
        steps={[
          'Add your course',
          'Check your settings',
          'Create the event',
          'Bring in your golfers',
          "Follow your format's guide",
          'Print and play',
          'Finalize the results',
        ]}
        note="Steps 1 to 4 can be done weeks ahead. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Your format:</strong> Captain&rsquo;s Choice (scramble), Best Ball, Shamble or
          Stroke Play. Skins and Closest to the Pin can run beside any of them.
        </LI>
        <LI>
          <strong>Shotgun or tee times</strong>, and roughly how many golfers you expect.
        </LI>
        <LI>
          <strong>Gross or net.</strong> Net needs each golfer&rsquo;s Handicap Index and the tees he
          plays (step 4).
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add your golf course" when="First — everything else needs it">
        <UL>
          <LI>
            Go to <Btn>⚙️ Admin</Btn> &rarr; <strong>Courses</strong> and click{' '}
            <Btn>+ Add Course</Btn>.
          </LI>
          <LI>
            <strong>Search first:</strong> type the course name to search more than 25,000 courses
            and import it. If it isn&rsquo;t there, enter the name, its location (for the weather)
            and 9 or 18 holes.
          </LI>
          <LI>
            <strong>Tees:</strong> open the course and check each set of tees your field will play:
            course rating, slope rating and par, plus the hole-by-hole par and hole handicap. To
            add one, click <Btn>+ Add Tee</Btn>; <Btn>📷 Import from Scorecard</Btn> (under Course
            Tees when you edit the course) reads it all from a photo of the scorecard.
          </LI>
          <LI>
            <strong>Card without a rating or slope?</strong> The import holds those tees until you
            type in the course rating and slope (from the pro shop or the course lookup on
            ghin.com). Photo tips and more are in{' '}
            <a href="/guides/getting-started-with-tee-time-organizer" style={{ color: '#166534', fontWeight: 700 }}>
              Getting Started, step 2
            </a>
            .
          </LI>
        </UL>
        <Heads>
          <strong>Why tees matter.</strong> The rating, slope and par turn each golfer&rsquo;s
          Handicap Index into his course handicap, and the hole handicaps go on the printed
          scorecards. Without them, net scoring and the cards can&rsquo;t be worked out.
        </Heads>
      </Step>

      <Step n={2} title="Check your settings" when="Two minutes, once">
        <UL>
          <LI>
            Open <Btn>🏢 Settings</Btn>. <strong>Location &amp; Time Zone</strong>: so times, dates,
            reminders and the weather are right for the course.
          </LI>
          <LI>
            <strong>Organizer Contact &amp; Email Settings:</strong> where golfers&rsquo; replies
            go.
          </LI>
          <LI>
            <strong>Identity &amp; Branding</strong> already carries your event&rsquo;s name. Change
            it here if you like.
          </LI>
        </UL>
      </Step>

      <Step n={3} title="Create the event" when="Any time before registration opens">
        <UL>
          <LI>
            On your dashboard click <Btn>+ New tee time</Btn>. Fill in the course, date and time.{' '}
            <strong>Slots</strong> is the size of your field.
          </LI>
          <LI>
            <strong>Start Type:</strong> <Btn>Shotgun</Btn> if everyone starts at once (you assign
            the starting holes later, on the Pairings page), or <Btn>Tee Times</Btn>.
          </LI>
          <LI>
            <strong>Invitation only?</strong> Tick <Btn>Private Tee Time</Btn>, so only the golfers
            you invite see it.
          </LI>
          <LI>
            Tick <Btn>This is an event</Btn> and click <Btn>Add Tee Time</Btn>. Your Event Pass
            attaches to this event.
          </LI>
          <LI>
            The <Btn>Set up event</Btn> window opens: the <strong>event name</strong> (the title on
            the leaderboard, scorecards and cart signs), the <strong>live leaderboard
            address</strong>, an optional <strong>logo</strong>, and the <strong>scoring</strong>{' '}
            (one score per team for a Captain&rsquo;s Choice). Click <Btn>Save event</Btn>.
          </LI>
        </UL>
        <Heads>
          <strong>Your pass sets the size of the field.</strong> The event badge in{' '}
          <Btn>Set up event</Btn> shows how many golfers it covers and how long it&rsquo;s good
          for. Need more than 72? <Btn>Upgrade to 250</Btn> is right there.
        </Heads>
      </Step>

      <Step n={4} title="Bring in your golfers" when="When you have your field, or to open registration">
        <H3>Option A: import your list (you already have the field)</H3>
        <UL>
          <LI>
            <Btn>⚙️ Admin</Btn> &rarr; <strong>Players</strong> &rarr; <Btn>📥 Import players</Btn>.
            Choose your spreadsheet (CSV or Excel), or copy the rows from it and paste them in. A
            blank template is there if you&rsquo;re starting from scratch.
          </LI>
          <LI>
            TTO recognizes the usual columns (first and last name or full name, email, phone, GHIN
            number, Handicap Index, gender, and an optional Role column: Player or Guest). Check
            them, and change any it got wrong.
          </LI>
          <LI>
            <strong>Add them as:</strong> <Btn>Guests</Btn>. <strong>Also put them on a tee
            time:</strong> choose your event. They&rsquo;re confirmed up to your field size; any
            extra go on the waitlist.
          </LI>
          <LI>
            The list shows what will happen to every row before you click{' '}
            <Btn>Import N golfers</Btn>. Nobody is emailed. Importing the same list again adds no
            one twice: golfers are matched by email, GHIN number or name.
          </LI>
        </UL>
        <H3>Option B: send them the invite link (they sign themselves up)</H3>
        <UL>
          <LI>
            On the tee time card click <Btn>📣 Publish</Btn>, choose <Btn>Send No Notifications</Btn>
            {' '}and publish.
          </LI>
          <LI>
            Click <Btn>🎟️ Invite</Btn> on the tee time card and copy the invite link. Put it in an
            email or text you write yourself, or on your event&rsquo;s website.
          </LI>
          <LI>
            Each golfer who opens the link signs himself up for the event and joins your account
            as a guest.
          </LI>
        </UL>
        <H3>Option C: add them one at a time</H3>
        <UL>
          <LI>
            <Btn>⚙️ Admin</Btn> &rarr; <strong>Players</strong> &rarr; <Btn>+ Add Player</Btn>. Keep
            the role as <strong>Guest</strong>: guests are free and never count toward your
            account&rsquo;s plan.
          </LI>
          <LI>
            Then add them to the event from <Btn>Manage players</Btn> on the tee time card.
          </LI>
        </UL>
        <Heads>
          <strong>For a net event, every golfer needs a Handicap Index and a preferred tee</strong>{' '}
          before the round. Check them in Admin &rarr; Players &rarr; edit each golfer, including
          the ones who came in through the invite link.
        </Heads>
      </Step>

      <Step n={5} title="Follow your format's guide" when="Once the field is set">
        <P>
          From here the steps depend on the format: building the teams on the Pairings page (and
          the shotgun starting holes), adding the game, issuing team codes and deciding ties. Open
          the guide for yours and pick up at its step for teams and starting holes. Steps 1 to 3
          there are the ones you just did.
        </P>
        <UL>
          {FORMAT_GUIDES.map((g) => (
            <LI key={g.slug}>
              <a href={`/guides/${g.slug}`} style={{ color: colors.green700, fontWeight: 700 }}>
                {g.title}
              </a>
              : {g.when}
            </LI>
          ))}
        </UL>
      </Step>

      <Step n={6} title="Print and play" when="The day before, and on the day">
        <UL>
          <LI>
            <Btn>🖨️ Print</Btn>: scorecards and cart signs, each with the team&rsquo;s starting hole
            (and its QR code, for team formats). The tee sheet and an event sign are there too.
          </LI>
          <LI>
            <strong>Keeping score:</strong> golfers score on their phones from their TTO link or
            by scanning the code on their card. No app to install, no password.
          </LI>
          <LI>
            <strong>Leaderboard:</strong> live at your event&rsquo;s leaderboard address on every
            phone. For the clubhouse TV, click <Btn>📺 TV</Btn> on the tee time card.
          </LI>
        </UL>
      </Step>

      <Step n={7} title="Finalize the results" when="When the last card is in">
        <UL>
          <LI>
            Check the cards against what is in TTO, correct any hole, and settle ties. Your
            format&rsquo;s guide has the details.
          </LI>
          <LI>
            Click <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>. This
            uses your Event Pass. The results and the leaderboard stay viewable for 30 days.
          </LI>
        </UL>
      </Step>

      <FAQ id="tournament-setup" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
